import { getSubEntityLabel } from '@zorro/common-utils';
import { Select } from '@zorro/design-system/v2/Select';
import { TagsInput } from '@zorro/design-system/v2/TagsInput';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import {
  FormErrorMessage,
  Grid,
  LabelWithTooltip,
} from '@zorro/web/design-system';
import { useMonolithQuery, useRoles } from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';

import { ApplicableLargeEmployerInput } from '../ApplicableLargeEmployerInput';
import { PayrollCycleInput } from '../PayrollCycleInput';
import { WaitingPeriodInput } from '../WaitingPeriodInput';
import styles from './CompanySetupForm.module.css';
import {
  CompanySetupFormFields,
  HRIS_PROVIDER_NOT_LISTED,
  filterHrisProviderOptions,
  getHrisProviderOptions,
} from './CompanySetupUtils';

type Props = {
  companySetupForm: UseFormReturn<CompanySetupFormFields>;
  isFinalizationMode?: boolean;
};

export const CompanySetupForm = ({
  companySetupForm,
  isFinalizationMode = false,
}: Props) => {
  'use no memo';
  const { isAgentWithoutSupervisor: isReadonlyMode } = useRoles();
  const { control, formState, watch } = companySetupForm;
  const { errors } = formState;
  const hrisProvider = watch('hrisProvider');
  const isNotListedProvider = hrisProvider === HRIS_PROVIDER_NOT_LISTED;

  const { data: finchProviders = [] } = useMonolithQuery({
    method: 'finchControllerGetAllProviders',
    params: [],
  });

  return (
    <Grid>
      <Grid.Col span={6}>
        <Controller
          name="payrollCycle"
          control={control}
          render={({ field }) => (
            <PayrollCycleInput
              {...field}
              selectProps={{
                required: isFinalizationMode,
                isReadOnly: isReadonlyMode,
              }}
              size="lg"
            />
          )}
        />
        <FormErrorMessage fieldName="payrollCycle" errors={errors} />
      </Grid.Col>
      <Grid.Col span={6}>
        <Controller
          name="hrisProvider"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              searchable
              clearable
              isReadOnly={isReadonlyMode}
              size="lg"
              label="HRIS / payroll system"
              placeholder="HRIS / payroll system"
              data={getHrisProviderOptions(finchProviders, hrisProvider)}
              filter={filterHrisProviderOptions}
            />
          )}
        />
        <FormErrorMessage fieldName="hrisProvider" errors={errors} />
        {isNotListedProvider && (
          <>
            <Controller
              name="hrisProviderNotListedText"
              control={control}
              render={({ field: { value, ...field } }) => (
                <TextInput
                  {...field}
                  value={value ?? ''}
                  isReadOnly={isReadonlyMode}
                  placeholder={
                    isReadonlyMode ? '-' : 'HRIS / payroll system name'
                  }
                  aria-label="HRIS / payroll system name"
                  size="lg"
                  mt="xs"
                />
              )}
            />
            <FormErrorMessage
              fieldName="hrisProviderNotListedText"
              errors={errors}
            />
          </>
        )}
      </Grid.Col>

      <Grid.Col span={6}>
        <Controller
          name="waitingPeriod"
          control={control}
          render={({ field }) => (
            <WaitingPeriodInput
              {...field}
              isReadOnly={isReadonlyMode}
              isRequired={isFinalizationMode}
            />
          )}
        />
        <FormErrorMessage fieldName="waitingPeriod" errors={errors} />
      </Grid.Col>
      <Grid.Col>
        <Controller
          name="isApplicableLargeEmployer"
          control={control}
          render={({ field }) => (
            <ApplicableLargeEmployerInput
              {...field}
              isReadOnly={isReadonlyMode}
              radioGroupProps={{ required: isFinalizationMode }}
            />
          )}
        />
        <FormErrorMessage
          fieldName="isApplicableLargeEmployer"
          errors={errors}
        />
      </Grid.Col>
      <Grid.Col>
        <Controller
          name="subEntities"
          control={control}
          render={({ field: { onBlur, onChange, ...rest } }) => (
            <TagsInput
              {...rest}
              onBlur={onBlur}
              onChange={(value) => {
                onChange(value);
                onBlur();
              }}
              isReadOnly={isReadonlyMode}
              className={styles.borderlessReadonly}
              placeholder={
                isReadonlyMode
                  ? '-'
                  : 'Type to add sub-entity names, separated by comma'
              }
              label={
                <LabelWithTooltip
                  label={getSubEntityLabel()}
                  tooltipLabel="Define your first set of sub-entity names (for example, branches, offices, or locations) to group employees. Enter each name separated by commas or press Enter after each one."
                />
              }
              size="lg"
            />
          )}
        />
        <FormErrorMessage fieldName="subEntities" errors={errors} />
      </Grid.Col>
      <Grid.Col>
        <Controller
          name="subEntities2"
          control={control}
          render={({ field: { onBlur, onChange, ...rest } }) => (
            <TagsInput
              {...rest}
              onBlur={onBlur}
              onChange={(value) => {
                onChange(value);
                onBlur();
              }}
              isReadOnly={isReadonlyMode}
              className={styles.borderlessReadonly}
              placeholder={
                isReadonlyMode
                  ? '-'
                  : 'Type to add sub-entity names, separated by comma'
              }
              label={
                <LabelWithTooltip
                  label="Sub-entity 2"
                  tooltipLabel="Define a second set of sub-entity names (for example, departments, teams, or cost centers) to further group employees. Enter each name separated by commas or press Enter after each one."
                />
              }
              size="lg"
            />
          )}
        />
        <FormErrorMessage fieldName="subEntities2" errors={errors} />
      </Grid.Col>
    </Grid>
  );
};
